<template>
  <div :class="section.StyleClass">
    <v-alert color="var(--red-100)" v-if="isSubmitError" class="error-message">
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div v-html="submitErrorMessage"></div>
      </div>
    </v-alert>
    <v-alert
      color="var(--red-100)"
      v-if="isCardPaymentFailed"
      class="error-message"
    >
      <div class="d-flex flex-no-wrap">
        <div class="mr-3">
          <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
        </div>
        <div v-html="failedCardPaymentErrorMessage"></div>
      </div>
    </v-alert>
    <div class="elevation-3 rounded-lg pa-4 pa-xl-16" style="background: white">
      <BaseButton
        :text="`Back to '${section.PreviousStepTitle}'`"
        flat
        ripple
        class="font-weight-bold pl-3 mt-2 d-flex back-btn back-previous__btn-link"
        icon="mdi-chevron-left"
        small
        color="#106175"
        @click="handleBack({ section })"
      />
      <!-- Stepper Title -->
      <v-row>
        <v-col class="d-flex justify-space-between flex-wrap">
          <div>
            <h1
              v-if="section.SectionTitleMobile"
              class="font-weight-bold font-28 section-title"
            >
              {{
                $vuetify.breakpoint.lgAndUp
                  ? section.SectionTitle
                  : section.SectionTitleMobile
              }}
            </h1>
            <h1 v-else class="font-weight-bold font-28 section-title">
              {{ section.SectionTitle }}
            </h1>
          </div>
          <BaseButton
            text="Save &amp; exit"
            flat
            depressed
            ripple
            :class="
              isSelfServe
                ? 'font-weight-bold d2c-save-exit mt-2 py-4 d-flex'
                : 'font-weight-bold mt-2 mr-2 px-1 py-3 d-flex'
            "
            icon="mdi-content-save"
            color="#106175"
            small
            @click="
              checkIsQuoteExpiredAndHandleNextAction(loadSaveAndExitDialog)
            "
          />
        </v-col>
      </v-row>

      <!-- Stepper content -->
      <v-row>
        <v-col>
          <v-form ref="form">
            <template v-for="subSection in section.SubSections">
              <div
                v-if="subSection.IsVisible"
                :key="subSection.SubSectionNumber"
              >
                <h3
                  v-if="subSection.SubSectionTitle.length"
                  class="font-20 sub-section-title padding-sub-section-title"
                >
                  {{ subSection.SubSectionTitle }}
                </h3>
                <template v-for="question in subSection.Questions">
                  <QuestionBase
                    :key="question.InterfaceKey"
                    :question="question"
                    @showDirectDebitRenewalInfo="showDirectDebitRenewalInfo"
                    @showPremiumBreakdownMonthly="showPremiumBreakdownMonthly"
                    @showPremiumBreakdown3Months="showPremiumBreakdown3Months"
                    @showPremiumBreakdownAnnual="showPremiumBreakdownAnnual"
                    @shareQuote="shareQuote"
                    @printQuote="printQuote"
                  />
                </template>
              </div>
            </template>
          </v-form>
        </v-col>
      </v-row>

      <!-- Stepper Controls -->
      <v-row
        :class="{
          'mt-19px': isSelfServe && section.SectionTitle === 'Payment',
        }"
      >
        <v-col :class="{ 'pt-2 mb-6': isSelfServe }">
          <!-- submit, continue to payment btns -->
          <div
            :class="{
              'd-flex justify-center d-lg-block': isSelfServe,
            }"
            v-if="showPaymentButtons"
          >
            <v-btn
              v-if="paymentMethodDirectDebit"
              :ripple="false"
              depressed
              rounded
              color="mustard"
              x-large
              class="btn-next font-20 font-weight-bold text-uppercase"
              :class="!isSelfServe ? 'mt-n2' : ''"
              :loading="isApplying"
              height="60px"
              :width="$vuetify.breakpoint.lgAndUp ? '261' : '100%'"
              @click="
                checkIsQuoteExpiredAndHandleNextAction(
                  handleSubmitDirectDebitApplication
                )
              "
            >
              <v-icon class="mr-2">mdi-lock</v-icon>
              <span v-if="isSelfServe">Pay now</span><span v-else>Submit</span>
            </v-btn>
            <v-btn
              v-else
              :ripple="false"
              depressed
              rounded
              color="mustard"
              x-large
              class="font-20 font-weight-bold text-uppercase black2-color"
              height="60px"
              :width="$vuetify.breakpoint.lgAndUp ? '372' : '100%'"
              :class="!isSelfServe ? 'mt-6' : ''"
              :loading="isApplying"
              @click="
                checkIsQuoteExpiredAndHandleNextAction(
                  handleSubmitCardPaymentApplication
                )
              "
            >
              <v-icon class="mr-2">mdi-lock</v-icon> Continue to payment
            </v-btn>
          </div>
          <div v-else>
            <v-btn
              :ripple="false"
              depressed
              rounded
              color="mustard"
              x-large
              class="btn-app-continue font-20 font-weight-bold text-uppercase"
              @click="checkIsQuoteExpiredAndHandleNextAction(clickNext)"
            >
              Continue to payment
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </div>
    <BaseDialog
      ref="directDebitRenewalInfo"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pb-5 mb-n1"
      headerDivider
    >
      <template v-slot:content>
        <DialogContentAutoRenewalInfoMobile />
      </template>
    </BaseDialog>
    <BaseDialog
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4"
    >
      <template v-slot:content="{ data }">
        <DialogContentPremiumBreakdown
          :breakdownModel="data"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>
    <BaseDialog
      ref="directDebitMissingBankDetails"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pb-5 mb-n1"
      headerDivider
      persistent
    >
      <template v-slot:content>
        <DialogContentMissingBankDetails
          @missingBankDetails="handleMissingBankDetails"
          @cancelMissingBankDetails="cancelMissingBankDetails"
          :sortCodeLength="sortCodeLength"
          :accountNumberLength="accountNumberLength"
        />
      </template>
    </BaseDialog>
    <BaseDialog
      ref="shareDialog"
      headerIcon="mdi-share-variant"
      headerIconColor="#106175"
    >
      <template v-slot:content="{ data }">
        <DialogContentShareQuotes
          :content="getCover"
          :quoteId="data"
          @success="shareQuotesSuccess"
        ></DialogContentShareQuotes>
      </template>
    </BaseDialog>
    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <BaseDialog
      ref="printQuotes"
      headerIcon="mdi-printer"
      headerIconColor="#106175"
      headerDivider
      windowClass="pa-0 pb-xl-0 mb-0"
      headerClass="px-7 pt-6 pb-3"
      contentClass="mb-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
      </template>
    </BaseDialog>
    <BaseDialog
      ref="saveAndExitDialog"
      headerIcon="mdi-content-save"
      headerIconColor="#106175"
      windowClass="pa-0 pb-xl-0"
      headerClass="d2c-dialog-header d2c-save-exit-header pa-4 mb-n1"
      headerDivider
      :max-width="800"
      :isSelfServe="isSelfServe"
      dark2Icon
    >
      <template v-slot:content="{}">
        <SaveAndExit
          :sectionReminder="section.SectionReminder"
          :showUserRef="false"
          @saveAndExit="saveAndExit"
          @close="closeSaveAndExitDialog"
        />
      </template>
    </BaseDialog>
    <BaseDialog
      ref="quoteExpiredDialog"
      windowClass="pa-0 pb-xl-0"
      headerClass="px-10 py-8 mb-n1"
      headerDivider
      :max-width="550"
    >
      <template v-slot:content="{ data }">
        <DialogContentQuoteExpired
          @close="closeQuoteExpiredDialog"
          :selectedQuote="data"
          :section="section"
        />
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { filterSingleQuoteDocs } from "@/common/docs";
import { getErrorMessage } from "@/common/utils";
import QuestionBase from "@/components/Questions/QuestionBase";
import { ERROR_MSG_FAILED_CARD_PAYMENT } from "@/constants/message.constants";
import documentService from "@/services/document.service";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
  STEPS_MODULE,
} from "@/store/types/module-types";
import { mapActions, mapGetters, mapState } from "vuex";
import { ANNUAL } from "@/constants/common.constants";
import { findError } from "@/common/question-set";
import { isQuoteExpired } from "@/common/quotes";

export default {
  name: "ApplicationQuestions",
  data() {
    return {
      isApplying: false,
      documentList: [],
      overlay: false,
      isSubmitError: false,
      isShowingDialogExpiryDateQuote: false,
      submitErrorMessage: "",
      failedCardPaymentErrorMessage: ERROR_MSG_FAILED_CARD_PAYMENT,
    };
  },
  components: {
    QuestionBase,
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    DialogContentAutoRenewalInfoMobile: () =>
      import("@/views/StageApplication/DialogContentAutoRenewalInfoMobile"),
    DialogContentPremiumBreakdown: () =>
      import("@/components/QuoteResult/DialogContentPremiumBreakdown.vue"),
    DialogContentMissingBankDetails: () =>
      import("@/views/StageApplication/DialogContentMissingBankDetails"),
    DialogContentShareQuotes: () =>
      import("@/components/QuoteResult/DialogContentShareQuotes.vue"),
    DialogContentPrintQuotes: () =>
      import("@/components/QuoteResult/DialogContentPrintQuotes.vue"),
    SaveAndExit: () => import("@/views/Dialog/SaveAndExit"),
    DialogContentQuoteExpired: () =>
      import("@/components/QuoteResult/DialogContentQuoteExpired.vue"),
  },
  mixins: [PremiumBreakdownMixin],
  props: {
    currentStep: {
      type: Number,
      required: true,
    },
    section: {
      type: Object,
      required: true,
    },
    sections: {
      type: Array,
      required: true,
    },
  },
  computed: {
    ...mapState(APPLICATION_MODULE, ["cardPaymentStatus", "questionMap"]),
    ...mapGetters(APPLICATION_MODULE, ["selectedQuote"]),
    ...mapGetters(QUESTIONS_MODULE, [
      "getQuoteRequestId",
      "httpConfig",
      "insurerQuotesModel",
      "getCover",
      "callChannel",
      "isSelfServe",
    ]),
    ...mapState(STEPS_MODULE, ["applicationStartingStep"]),
    ...mapGetters(STEPS_MODULE, ["totalSteps", "getPaymentDetailStep"]),
    paymentMethodDirectDebit() {
      return this.questionMap.PaymentMethod.Value === "DirectDebit";
    },
    paymentFrequency() {
      // Return undefined instead of an empty string if no Payment Frequency has been selected,
      // so that the default of "monthly" can be applied downstream in docs.js.
      return this.questionMap.PaymentFrequency.Value || undefined;
    },
    paymentFrequencyForDocs() {
      // Prevent inheriting the previous Payment Frequency selection when Payment Method is changed to card.
      return this.questionMap.PaymentMethod.Value === "CreditCard"
        ? ANNUAL
        : this.paymentFrequency;
    },
    sortCodeLength() {
      return this.questionMap.BankSortCode?.Value?.length;
    },
    accountNumberLength() {
      return this.questionMap.BankAccountNo?.Value?.length;
    },
    showPaymentButtons() {
      return (
        this.section.SectionNumber < this.totalSteps &&
        this.section.SectionNumber === this.getPaymentDetailStep
      );
    },
    isCardPaymentFailed() {
      return this.cardPaymentStatus === "failed";
    },
  },
  methods: {
    ...mapActions(STEPS_MODULE, [
      "handleBack",
      "handleNext",
      "handleNextWithoutRef",
    ]),
    ...mapActions(QUESTIONS_MODULE, ["saveQuoteResults"]),
    ...mapActions(APPLICATION_MODULE, [
      "handleSaveAndExit",
      "handleD2CSaveAndExit",
      "handleApplicationSubmission",
      "proceedToCardPayment",
      "setSessionStorageItems",
      "setCardPaymentStatus",
      "resetInvalidQuestions",
    ]),
    clearCardPaymentStatus() {
      this.setCardPaymentStatus("");
    },
    clickNext() {
      this.handleNext({
        section: this.section,
        ref: this.$refs.form,
      });
    },
    loadSaveAndExitDialog() {
      this.$refs.saveAndExitDialog.open("Save & exit");
    },

    async validateForm() {
      const form = this.$refs.form;
      const isValid = form.validate();

      if (!isValid) {
        const errors = findError(form);
        this.resetInvalidQuestions(errors);
      }
    },
    async saveAndExit() {
      this.$refs.saveAndExitDialog.cancel();
      if (this.isSelfServe) {
        await this.validateForm();
        this.handleD2CSaveAndExit();
      } else {
        this.handleSaveAndExit();
      }
    },
    closeSaveAndExitDialog() {
      this.$refs.saveAndExitDialog.cancel();
    },
    handleSubmitCardPaymentApplication() {
      this.clearCardPaymentStatus();
      if (this.validateApplicationForm()) {
        this.submitCardPaymentApplication();
      }
    },
    submitCardPaymentApplication() {
      this.isApplying = true;
      this.isSubmitError = false;
      this.handleApplicationSubmission()
        .then(() => {
          this.setSessionStorageItems();
          this.proceedToCardPayment();
        })
        .catch((error) => {
          this.isSubmitError = true;
          this.submitErrorMessage = getErrorMessage(error);
          this.isApplying = false;
          console.log("Apply error:", error);
        });
    },
    handleSubmitDirectDebitApplication() {
      this.clearCardPaymentStatus();
      if (this.validateApplicationForm()) {
        if (!!this.sortCodeLength && !!this.accountNumberLength) {
          this.submitDirectDebitApplication();
        } else {
          this.$refs.directDebitMissingBankDetails.open("Missing bank details");
        }
      } else {
        setTimeout(() => {
          const errorText = document.querySelector("div.error--text");
          const input = errorText?.querySelector("input");
          input?.scrollIntoView({
            block: "center",
            behavior: "smooth",
          });
        }, 100);
      }
    },
    submitDirectDebitApplication() {
      this.isApplying = true;
      this.isSubmitError = false;
      this.handleApplicationSubmission()
        .then(() => {
          this.setSessionStorageItems();
          this.isApplying = false;
          const routeName = this.isSelfServe ? "Success" : "Complete";
          this.$router.push({ name: routeName });
        })
        .catch((error) => {
          this.isSubmitError = true;
          this.submitErrorMessage = getErrorMessage(error);
          this.isApplying = false;
          console.error("Apply error:", error);
        });
    },
    handleMissingBankDetails() {
      this.$refs.directDebitMissingBankDetails.cancel();
      this.submitDirectDebitApplication();
    },
    showDirectDebitRenewalInfo() {
      this.$refs.directDebitRenewalInfo.open("Stay covered");
    },
    validateApplicationForm() {
      return this.$refs.form.validate();
    },
    cancelMissingBankDetails() {
      this.$refs.directDebitMissingBankDetails.cancel();
    },
    async shareQuote(quoteId) {
      await this.saveQuoteResults();

      this.$refs.shareDialog.open("Share via email", quoteId);
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    async getDocumentList() {
      await this.saveQuoteResults().catch((e) => {
        throw e;
      });
      if (this.documentList.length) {
        return this.documentList;
      }
      return documentService
        .getDocumentList(
          this.getQuoteRequestId,
          this.callChannel,
          this.httpConfig
        )
        .then((res) => {
          if (res.StatusCode === "OK") {
            res.Documents.forEach((doc) => {
              doc.isDownloading = false;
              doc.isDownloaded = false;
            });

            //The code below is to have a copy of these files for each quote so we could track their download state.
            //only two records of ipid and terms are coming back from api, we need one for each quote for their download state.
            const termFile = res.Documents.find((doc) => doc.Code === "terms");
            const ipidFile = res.Documents.find((doc) => doc.Code === "ipid");
            const factfindsdn = res.Documents.find(
              (doc) => doc.Code === "factfindsdn"
            );
            this.insurerQuotesModel.forEach((quote) => {
              if (quote.isAvailable) {
                quote.quotes.forEach((insurerQuote) => {
                  res.Documents.push({
                    ...termFile,
                    QuoteId: insurerQuote.QuoteId,
                  });
                  res.Documents.push({
                    ...ipidFile,
                    QuoteId: insurerQuote.QuoteId,
                  });
                  res.Documents.push({
                    ...factfindsdn,
                    QuoteId: insurerQuote.QuoteId,
                  });
                });
              }
            });
            this.documentList = res.Documents;
            return this.documentList;
          }
        })
        .catch((error) => {
          throw error;
        });
    },
    printQuote(quote) {
      this.overlay = true;
      this.getDocumentList()
        .then(() => {
          const data = {
            ...quote,
            files: this.documentList.filter((doc) =>
              filterSingleQuoteDocs(
                doc,
                quote.QuoteId,
                this.paymentFrequencyForDocs
              )
            ),
            type: "single",
          };
          this.overlay = false;
          this.$refs.printQuotes.open("Download for printing", data);
        })
        .catch((error) => {
          this.overlay = false;
          console.error(error);
        });
    },
    showQuoteExpiredDialog() {
      this.$refs?.quoteExpiredDialog?.open("Quote expired", this.selectedQuote);
    },
    closeQuoteExpiredDialog() {
      this.$refs?.quoteExpiredDialog?.cancel();
    },
    checkIsQuoteExpiredAndHandleNextAction(nextAction) {
      if (isQuoteExpired(this.selectedQuote)) {
        this.showQuoteExpiredDialog();
      } else if (nextAction) {
        nextAction();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.back-btn {
  margin-top: -33px !important;
  margin-left: -63px !important;
  margin-bottom: 29px !important;
}

.v-form div:first-child {
  .padding-sub-section-title {
    padding-top: 0;
  }
}

.btn-continue {
  margin-top: 19px !important;
  margin-bottom: -24px !important;
}
.btn-app-continue {
  height: 60px !important;
  color: $black-2 !important;
  margin-top: -8px;
}
.font-weight-600 {
  font-weight: 600;
}
.sub-section-title {
  color: #272727 !important;
}
.padding-sub-section-title {
  padding: 24px 0 10px 0;
}
.section-title {
  color: #272727 !important;
}

.d2c-payment {
  .d2c-save-exit {
    margin-right: -17px;
  }
  color: $black-2;
  :deep(.theme--light.v-card) {
    color: $black-2 !important;
  }
  :deep(.btn-next.theme--light.v-btn) {
    color: $black-2 !important;
  }
  :deep(.radioButton) {
    .v-btn.v-btn--outlined.v-btn--rounded:not(.radioButton--active) {
      .v-btn__content {
        color: #2a2a2a !important;
      }
    }
  }
  :deep(.v-application .black--text) {
    color: $black-2 !important;
  }
  :deep(.theme--light.v-sheet) {
    color: #272727 !important;
  }
}

.mt-19px {
  margin-top: 19px;
}

@media #{map-get($display-breakpoints, 'md-and-up')} {
  .btn-app-continue {
    min-width: 390px !important;
  }
}

// lg breakpoint
@media screen and (max-width: 992px) {
  .font-weight-600 {
    font-weight: 600;
  }
  .back-btn {
    margin-top: 11px !important;
    margin-left: -20px !important;
    margin-bottom: 10px !important;
  }
  .btn-continue {
    width: 100%;
    margin-top: 19px !important;
    margin-bottom: 24px !important;
  }
  .btn-app-continue {
    width: 100%;
    margin-top: 7px !important;
    margin-bottom: 24px !important;
  }
}

@media screen and (max-width: ($grid-breakpoints-sm - 1)) {
  .v-btn.btn-next {
    width: 100%;
  }
}
.error-message {
  border: solid 2px var(--v-error-base) !important;
}
</style>
